import React, { useEffect, useRef, useState, type CSSProperties } from 'react'

import { ResponsiveImage } from '@/components/Shared/ResponsiveImage'
import { useInViewport } from '@/hooks/useInViewport'
import type { VideoProps } from '@/types/components/video'

import './video.scss'

export const Video: React.FC<VideoProps> = ({
  videoUrl,
  videoType,
  ratios,
  dialogueThumbnail,
  includeControls,
  loopVideo,
  captionsUrl,
  backupImage,
  classes,
  containerClasses,
  isFirstSection = false,
  buttonType = 'default',
  onPlayingChange,
}) => {
  const isBackgroundVideo = videoType === 'background'
  const isDialogueVideo = !isBackgroundVideo

  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const { ref: containerRef, hasBeenInViewport } =
    useInViewport<HTMLDivElement>({
      rootMargin: '200px 0px',
    })

  useEffect(() => {
    onPlayingChange?.(isPlaying)
  }, [isPlaying])

  const handlePlayPause = () => {
    if (!videoRef.current) return

    if (isPlaying) {
      videoRef.current.pause()
    } else {
      videoRef.current.play()
    }

    setIsPlaying(!isPlaying)
  }

  const source =
    isDialogueVideo && !dialogueThumbnail?.desktopImage?.asset.url
      ? `${videoUrl}#t=0.001` // Makes sure that the first frame of the video is shown as the poster
      : videoUrl
  const posterImage = isDialogueVideo
    ? dialogueThumbnail
    : backupImage || backupImage
  const videoStyle: CSSProperties & { [key: string]: string } = {
    '--mobile-ratio': ratios?.mobileRatio?.videoRatio || 'auto',
    '--desktop-ratio': ratios?.desktopRatio?.videoRatio || 'auto',
  }

  return (
    <div
      ref={containerRef}
      className={`video-container ${containerClasses || ''}`}
    >
      {isFirstSection || hasBeenInViewport ? (
        <video
          ref={videoRef}
          crossOrigin={`${isDialogueVideo && captionsUrl ? 'anonymous' : ''}`}
          autoPlay={isBackgroundVideo}
          muted={isBackgroundVideo}
          loop={isBackgroundVideo && loopVideo}
          controls={isPlaying && isDialogueVideo && includeControls}
          className={classes}
          playsInline
          poster={posterImage?.desktopImage?.asset.url || ''}
          style={videoStyle}
        >
          <source src={source} />
          {isDialogueVideo ? (
            <track kind="captions" src={captionsUrl} srcLang="en" default />
          ) : null}
          <ResponsiveImage
            document={backupImage}
            overrides={{
              loading: isFirstSection ? 'eager' : 'lazy',
              customSizes: [640, 768, 1024, 1200],
            }}
          />
        </video>
      ) : (
        <ResponsiveImage
          document={posterImage || backupImage}
          overrides={{
            loading: isFirstSection ? 'eager' : 'lazy',
            customSizes: [640, 768, 1024, 1200],
          }}
        />
      )}
      {isDialogueVideo &&
        !isPlaying &&
        (buttonType === 'primary' ? (
          <button
            onClick={handlePlayPause}
            className="video-play-button button button--primaryLime button--small opacity-100"
          >
            Play ▶
          </button>
        ) : (
          <button onClick={handlePlayPause} className="video-play-button">
            <div className="triangle-right"></div>
          </button>
        ))}
    </div>
  )
}
